/**
 * Bottom-Navigation – nur auf Mobile sichtbar (< 768px, via CSS).
 *
 * NT-LAYOUT-01 (L2): fünf Spiel-Bereiche, die man ständig braucht —
 * KARRIERE · LABOR · GEWÄCHSHAUS · ROLLTISCH · VIERTEL. Business, Immobilien, Community,
 * Organisation, Profil und Optionen sind nur noch über das Hamburger-Menü erreichbar.
 *
 * Labor und Gewächshaus bleiben auch gesperrt sichtbar (gedämpft) — antippen öffnet die
 * Sperr-Ansicht von Crime Ops mit Freischalt-Hinweis. Nichts wird ausgeblendet, damit die Nav
 * nicht je nach Spielstand springt.
 *
 * Active-State wird aus window.location.pathname abgeleitet.
 */

import { navigate, onRouteChange } from '../router.js';
import { api } from '../services/api.js';
import { gameState } from '../state/gameState.js';
import { CRIME_OPS_SECTION_PATHS, JOBS_VIEW_PATHS } from '../utils/dashboard-routes.js';

type BottomTab = 'karriere' | 'labor' | 'gewaechshaus' | 'rolltisch' | 'viertel';

interface TabConfig {
  readonly id: BottomTab;
  readonly label: string;
  readonly icon: string;
  readonly defaultPath: string;
  // Pfade die diesem Tab zugeordnet sind (für Active-State-Erkennung)
  readonly paths: readonly string[];
  // Schlüssel in /crime-ops/status, falls der Bereich gesperrt sein kann
  readonly lockKey?: 'labor' | 'gewächshaus';
}

const ICON_CAREER = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>`;

// Erlenmeyerkolben
const ICON_LABOR = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 3h6"/><path d="M10 3v6.5L4.5 19a1.5 1.5 0 0 0 1.3 2h12.4a1.5 1.5 0 0 0 1.3-2L14 9.5V3"/><path d="M7 15h10"/></svg>`;

// Pflanze
const ICON_GREENHOUSE = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 21v-9"/><path d="M12 12C12 8 9 5 4 5c0 4 3 7 8 7z"/><path d="M12 14c0-4 3-7 8-7 0 4-3 7-8 7z"/><path d="M7 21h10"/></svg>`;

// Gerollte Zigarette
const ICON_ROLLTISCH = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="2" y="13" width="17" height="4" rx="1"/><path d="M15 13v4"/><path d="M21 13v4"/><path d="M18 9c0-2 2-2 2-4"/></svg>`;

// Stadtplan / Straßenraster
const ICON_VIERTEL = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 6l6-3 6 3 6-3v15l-6 3-6-3-6 3z"/><path d="M9 3v15"/><path d="M15 6v15"/></svg>`;

const TABS: readonly TabConfig[] = [
  {
    id: 'karriere',
    label: 'KARRIERE',
    icon: ICON_CAREER,
    defaultPath: JOBS_VIEW_PATHS.list,
    paths: [...Object.values(JOBS_VIEW_PATHS), '/talentbaum', '/dashboard'],
  },
  {
    id: 'labor',
    label: 'LABOR',
    icon: ICON_LABOR,
    defaultPath: CRIME_OPS_SECTION_PATHS.labor,
    paths: [CRIME_OPS_SECTION_PATHS.labor],
    lockKey: 'labor',
  },
  {
    id: 'gewaechshaus',
    label: 'GEWÄCHSHAUS',
    icon: ICON_GREENHOUSE,
    defaultPath: CRIME_OPS_SECTION_PATHS['gewächshaus'],
    paths: [CRIME_OPS_SECTION_PATHS['gewächshaus']],
    lockKey: 'gewächshaus',
  },
  {
    id: 'rolltisch',
    label: 'ROLLTISCH',
    icon: ICON_ROLLTISCH,
    defaultPath: CRIME_OPS_SECTION_PATHS.rolltisch,
    paths: [CRIME_OPS_SECTION_PATHS.rolltisch],
  },
  {
    id: 'viertel',
    label: 'VIERTEL',
    icon: ICON_VIERTEL,
    defaultPath: CRIME_OPS_SECTION_PATHS.viertel,
    paths: [CRIME_OPS_SECTION_PATHS.viertel],
  },
] as const;

// Freischalt-Status nur gelegentlich nachladen — /crime-ops/status ist kein billiger Endpunkt.
const LOCK_REFRESH_MIN_INTERVAL_MS = 60_000;

interface CrimeOpsLockStatus {
  labor: { unlocked: boolean };
  gewächshaus: { unlocked: boolean };
}

const lockedKeys = new Set<string>();
let lastLockRefreshAt = 0;
let lockRefreshInFlight = false;

/** Aktiven Tab aus aktuellem Pfad ableiten */
function getActiveTab(path: string): BottomTab | null {
  for (const tab of TABS) {
    if (tab.paths.includes(path)) return tab.id;
  }
  return null;
}

/** Gesperrt-Zustand (gedämpft) im DOM aktualisieren */
function applyLockState(): void {
  const nav = document.getElementById('bottom-nav');
  if (!nav) return;
  for (const tab of TABS) {
    if (!tab.lockKey) continue;
    const el = nav.querySelector<HTMLElement>(`[data-tab="${tab.id}"]`);
    el?.classList.toggle('is-locked', lockedKeys.has(tab.lockKey));
  }
}

/** Freischalt-Status von Labor/Gewächshaus nachladen (gedrosselt, still bei Fehler). */
async function refreshLockState(force = false): Promise<void> {
  if (!gameState.isAuthenticated() || lockRefreshInFlight) return;
  if (!force && Date.now() - lastLockRefreshAt < LOCK_REFRESH_MIN_INTERVAL_MS) return;
  lockRefreshInFlight = true;
  try {
    const status = await api.get<CrimeOpsLockStatus>('/crime-ops/status');
    lockedKeys.clear();
    if (!status.labor.unlocked) lockedKeys.add('labor');
    if (!status.gewächshaus.unlocked) lockedKeys.add('gewächshaus');
    lastLockRefreshAt = Date.now();
    applyLockState();
  } catch {
    // Status nicht verfügbar — Nav bleibt ungedämpft, Sperr-Ansicht greift trotzdem beim Antippen
  } finally {
    lockRefreshInFlight = false;
  }
}

/** Aktiven Tab im DOM aktualisieren */
export function updateBottomNavTab(path: string): void {
  const nav = document.getElementById('bottom-nav');
  if (!nav) return;
  const activeId = getActiveTab(path);
  nav.querySelectorAll<HTMLElement>('[data-tab]').forEach((el) => {
    el.classList.toggle('active', el.dataset.tab === activeId);
  });
}

/** Bottom-Nav einmalig erstellen und an body anhängen */
export function initBottomNav(): void {
  if (document.getElementById('bottom-nav')) return;

  const nav = document.createElement('nav');
  nav.id = 'bottom-nav';
  nav.setAttribute('aria-label', 'Haupt-Navigation');

  nav.innerHTML = TABS.map((tab) => `
    <button
      class="bottom-nav-tab"
      data-tab="${tab.id}"
      aria-label="${tab.label}"
      type="button"
    >
      <span class="bottom-nav-icon" aria-hidden="true">${tab.icon}</span>
      <span class="bottom-nav-label">${tab.label}</span>
    </button>
  `).join('');

  document.body.appendChild(nav);

  nav.addEventListener('click', (e: MouseEvent) => {
    const btn = (e.target as Element).closest<HTMLElement>('[data-tab]');
    if (!btn) return;
    const tabId = btn.dataset.tab as BottomTab;
    const tab = TABS.find((t) => t.id === tabId);
    if (!tab) return;
    // Bereits dort → kein doppelter History-Eintrag
    if (window.location.pathname === tab.defaultPath) return;
    navigate(tab.defaultPath);
  });

  onRouteChange((path) => {
    updateBottomNavTab(path);
    void refreshLockState();
  });
  updateBottomNavTab(window.location.pathname);

  // Nach Login/Logout den Sperr-Zustand neu bewerten
  gameState.on('auth:logout', () => {
    lockedKeys.clear();
    lastLockRefreshAt = 0;
    applyLockState();
  });
  gameState.on('player:updated', () => { void refreshLockState(); });
}

/**
 * Aktiven Tab direkt per ID setzen – Legacy-Fallback für Dashboard-interne Nutzung.
 * Mit URL-basierter Navigation wird dies nicht mehr aktiv aufgerufen.
 */
export function setBottomNavActiveTab(_tabId: string): void {
  updateBottomNavTab(window.location.pathname);
}
